import {Item} from '@PosterWhiteboard/items/item/item.class';
import {Rect, util} from '@postermywall/fabricjs-2';
import type {BackgroundConversionResetsObject, BaseItemObject, CopyableItemStyles, InitItemOpts, ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {RGBA} from '@Utils/color.util';
import {convertFabricColorToFillObject, getRgbaArrayFromFabricColor} from '@Utils/color.util';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import type {DeepPartial} from '@/global';

export interface RectangleItemObject extends BaseItemObject {
  rx: number;
  ry: number;
}

const DEFAULT_DIMENSION = 300;

export class RectangleItem extends Item {
  declare _fabricObject: Rect;
  public gitype: ITEM_TYPE.RECTANGLE = ITEM_TYPE.RECTANGLE;

  override get fabricObject(): Rect {
    return this._fabricObject;
  }

  public override toObject(): RectangleItemObject {
    return {
      ...super.toObject(),
      rx: this.fabricObject.rx,
      ry: this.fabricObject.ry,
    };
  }

  public override getCopyableStyles(): CopyableItemStyles {
    return {
      ...super.getCopyableStyles(),
      border: this.border.toObject(),
      frontFill: this.toObject().fill,
      textBorder: this.border.toObject(),
      rx: this.fabricObject.rx,
      ry: this.fabricObject.ry,
    };
  }

  public override async pasteStyles(copiedStyles: CopyableItemStyles): Promise<void> {
    const stylesObj: DeepPartial<RectangleItemObject> = {};
    if (copiedStyles.textBorder !== undefined) {
      stylesObj.border = this.getBorderFromCopiedStyles(copiedStyles.textBorder);
    }

    if (copiedStyles.frontFill !== undefined) {
      stylesObj.fill = copiedStyles.frontFill;
    }
    if ('rx' in copiedStyles) {
      stylesObj.rx = copiedStyles.rx;
    }
    if ('ry' in copiedStyles) {
      stylesObj.ry = copiedStyles.ry;
    }

    const commonStylesObj: DeepPartial<ItemObject> = this.createCommonStylesObject(copiedStyles, ['scaleX', 'scaleY']);
    await this.applyPastedStyles({...commonStylesObj, ...stylesObj});
  }

  protected override isRoundedBorderSupported(): boolean {
    return false;
  }

  public override isTransparentBackground(): boolean {
    return this.isBackground && convertFabricColorToFillObject(this.getFillObject()).fillType === FillTypes.NONE;
  }

  /**
   * Roundness has no background UI, and rounded corners punch transparent notches into a
   * full-bleed color background.
   */
  public override getBackgroundConversionResets(): BackgroundConversionResetsObject & Pick<RectangleItemObject, 'rx' | 'ry'> {
    return {
      ...super.getBackgroundConversionResets(),
      rx: 0,
      ry: 0,
    };
  }

  public override getColors(): RGBA[] {
    const colors: RGBA[] = super.getColors();

    if (this.fabricObject.fill) {
      colors.push(...getRgbaArrayFromFabricColor(this.getFillObject()));
    }

    colors.push(this.border.solidBorderColor);

    return colors;
  }

  protected override async updateFabricObjectFromObject(obj: DeepPartial<RectangleItemObject>): Promise<void> {
    await super.updateFabricObjectFromObject(obj);

    if (obj.fill !== undefined) {
      const unserializedObj = await util.enlivenObjectEnlivables({
        fill: obj.fill,
      });
      this.fabricObject.set('fill', unserializedObj.fill);
    }

    if (obj.rx !== undefined) {
      this.fabricObject.set('rx', obj.rx);
    }

    if (obj.ry !== undefined) {
      this.fabricObject.set('ry', obj.ry);
    }
  }

  protected override async getFabricObjectForItem(opts?: InitItemOpts): Promise<Rect> {
    const {shadow} = await util.enlivenObjectEnlivables({
      shadow: opts?.shadow ?? null,
    });

    const rect = new Rect({
      width: opts?.width ?? DEFAULT_DIMENSION,
      height: opts?.height ?? DEFAULT_DIMENSION,
      uniformScaling: false,
      uniformRoundness: true,
      scaleX: opts?.scaleX ?? 1,
      scaleY: opts?.scaleY ?? 1,
    });

    rect.set('shadow', shadow);

    return rect;
  }
}
